<template>
  <div class="relative searchable-multi-select">
    <!-- Search Input -->
    <div class="relative">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
        <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="placeholder"
        class="form-input w-full"
        style="padding-left: 2.5rem;"
        @focus="showDropdown = true">
      <button
        v-if="selectedItems.length > 0"
        type="button"
        @click="clearAll"
        class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Selected Items Pills -->
    <div v-if="selectedItems.length > 0" class="mt-2 flex flex-wrap gap-2">
      <span
        v-for="item in selectedItems"
        :key="getItemValue(item)"
        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {{ getItemLabel(item) }}
        <button
          type="button"
          @click="removeItem(item)"
          class="ml-1 inline-flex items-center p-0.5 rounded-full hover:bg-blue-200">
          <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </span>
    </div>

    <!-- Dropdown Options -->
    <div
      v-show="showDropdown && filteredOptions.length > 0"
      class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md border border-gray-300 overflow-auto">
      <div
        v-for="option in filteredOptions"
        :key="getItemValue(option)"
        @click="toggleOption(option)"
        class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
        <input
          type="checkbox"
          :checked="isSelected(option)"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded pointer-events-none">
        <span class="ml-3 text-sm text-gray-700">
          {{ getItemLabel(option) }}
        </span>
      </div>
    </div>

    <!-- No results message -->
    <div
      v-show="showDropdown && searchQuery && filteredOptions.length === 0"
      class="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 p-3">
      <p class="text-sm text-gray-500 text-center">
        No results found for "{{ searchQuery }}"
      </p>
    </div>

    <!-- Selection count -->
    <p v-if="selectedItems.length > 0" class="mt-1 text-sm text-gray-500">
      {{ selectedItems.length }} selected
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    required: true
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  labelKey: {
    type: String,
    default: 'name'
  },
  valueKey: {
    type: String,
    default: 'id'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const searchQuery = ref('')
const showDropdown = ref(false)

const getItemLabel = (item) => {
  if (typeof item === 'object') {
    return item[props.labelKey] || item[props.valueKey] || String(item)
  }
  return String(item)
}

const getItemValue = (item) => {
  if (typeof item === 'object') {
    return item[props.valueKey] || item[props.labelKey] || String(item)
  }
  return String(item)
}

const selectedItems = computed(() => props.options.filter((option) => props.modelValue.includes(getItemValue(option))))

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }

  const query = searchQuery.value.toLowerCase()
  return props.options.filter((option) => {
    const label = getItemLabel(option).toLowerCase()
    const value = String(getItemValue(option)).toLowerCase()
    return label.includes(query) || value.includes(query)
  })
})

const isSelected = (option) => props.modelValue.includes(getItemValue(option))

const toggleOption = (option) => {
  const value = getItemValue(option)
  const newValue = isSelected(option) ?
    props.modelValue.filter((v) => v !== value) :
    [...props.modelValue, value]

  emit('update:modelValue', newValue)
  emit('change', newValue)
}

const removeItem = (item) => {
  const value = getItemValue(item)
  const newValue = props.modelValue.filter((v) => v !== value)
  emit('update:modelValue', newValue)
  emit('change', newValue)
}

const clearAll = () => {
  emit('update:modelValue', [])
  emit('change', [])
}

const handleClickOutside = (e) => {
  const dropdown = e.target.closest('.searchable-multi-select')
  if (!dropdown) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
